import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { Step, StepContent, StepLabel, Stepper } from "@mui/material";
import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import StyledCheckbox from "components/shared/StyledCheckbox";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { Body, H3 } from "helpers/TypographyComponents";
import { getCognitoUserAttribute } from "helpers/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { IUserData } from "store/settingsStore";
import { putMeVerifiedOrAcceptedTos, putMeVerifiedOrAcceptedTosBP } from "store/settingsStore";
import { useAppDispatch } from "store/store";

interface VerificationModalProps {
    userData: IUserData | null;
}

const VerificationModal = (props: VerificationModalProps) => {
    const { userData } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useAuthenticator((context) => [context.user]);
    const accountType = getCognitoUserAttribute("custom:accountType", user);

    const [openTextField, setOpenTextField] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationStatus, setVerificationStatus] = useState({
        isVerified: !!(userData?.isVerified ?? false),
        hasAcceptedToS: false,
    });
    const [activeStep, setActiveStep] = useState(0);
    const [hasSentVerificationCodeReq, setHasSentVerificationCodeReq] = useState(false);

    const isNotValidAccount = userData != null && (!userData.isVerified || !userData.hasAcceptedToS);
    const isVerificationANumber = verificationCode.length == 0 || /^\d+$/.test(verificationCode);

    useEffect(() => {
        if (isNotValidAccount && !hasSentVerificationCodeReq) {
            user.getAttributeVerificationCode("email", {
                onSuccess: (success) => {},
                onFailure: (err) => {
                    toast.error(`${t("Settings.SomethingWentWrong")}`);
                },
            });
            setHasSentVerificationCodeReq(true);
        }
    }, [isNotValidAccount, hasSentVerificationCodeReq]);

    const handleOnSendClick = () => {
        if (!verificationCode) return;
        user.verifyAttribute("email", verificationCode, {
            onSuccess: (success) => {
                setOpenTextField(false);
                toast.success(`${t("Settings.EmailVerified")}`);
                setActiveStep(1);
                setVerificationCode("");
                setVerificationStatus({ ...verificationStatus, isVerified: true });
            },
            onFailure: (err) => {
                if (err.name == "CodeMismatchException") return toast.error(`${t("Settings.WrongCode")}`);
                toast.error(`${t("Settings.SomethingWentWrong")}`);
            },
        });
    };

    const handleConfirm = () => {
        if (!verificationStatus.hasAcceptedToS) return toast.error(`${t("Verification.AcceptTosError")}`);
        if (!verificationStatus.isVerified) return toast.error(`${t("Verification.VerifyEmailError")}`);
        if (accountType === "beneficiary") dispatch(putMeVerifiedOrAcceptedTosBP(verificationStatus));
        else dispatch(putMeVerifiedOrAcceptedTos(verificationStatus));
    };

    return (
        <ModalBox
            onClose={() => {}}
            open={isNotValidAccount}
            // userData != null && (!userData.isVerified || !userData.hasAcceptedToS)
            mainTitle={t("Verification.Warning")}
            subTitle={t("Verification.WarningText")}
            showCloseButton={false}
            style={{ width: "725px" }}
        >
            <div className="verification-modal">
                <div>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        <Step>
                            <StepLabel>
                                <H3 color={activeStep == 0 ? "black" : undefined}>{t("Verification.Step1Title")}</H3>
                            </StepLabel>
                            <StepContent>
                                <Body>
                                    {t("Verification.Step1Description1")}
                                    <br />
                                    {t("Verification.Step1Description2")}
                                </Body>
                                <StyledTextField
                                    value={verificationCode}
                                    fullWidth
                                    placeholder={t("Verification.VerificationCode")}
                                    error={!isVerificationANumber}
                                    helperText={
                                        !isVerificationANumber && t("Verification.VerificationCodeMustBeANumber")
                                    }
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    style={{ margin: "8px 0" }}
                                />
                                <StyledButton
                                    buttonType="primary"
                                    disabled={openTextField && !verificationCode}
                                    onClick={handleOnSendClick}
                                    allowViewOnly
                                >
                                    {t("Validate")}
                                </StyledButton>
                            </StepContent>
                        </Step>

                        <Step>
                            <StepLabel>
                                <H3 color={activeStep == 1 ? "black" : undefined}>{t("Verification.Step2Title")}</H3>
                            </StepLabel>
                            <StepContent>
                                <div className="tos">
                                    <StyledCheckbox
                                        id="tos"
                                        onChange={(e) =>
                                            setVerificationStatus({
                                                ...verificationStatus,
                                                hasAcceptedToS: e.target.checked,
                                            })
                                        }
                                    ></StyledCheckbox>
                                    <H3 tag="label" htmlFor="tos" color="black">
                                        {t("Verification.Step2Description")}{" "}
                                        <a
                                            href={`${window.location.origin}/cgu_crealo.pdf`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t("LogIn.TOS")}
                                        </a>
                                    </H3>
                                </div>
                            </StepContent>
                        </Step>
                    </Stepper>
                </div>
                <div className="verification-modal-buttons">
                    <StyledButton
                        buttonType="primary"
                        fullWidth
                        disabled={!verificationStatus.hasAcceptedToS || !verificationStatus.isVerified}
                        onClick={handleConfirm}
                        allowViewOnly
                    >
                        {t("Verification.AccessCrealo")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default VerificationModal;
